import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useEntityDelete } from '@/shared/hooks/useEntityDelete';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiPartiesDelete } from '@/entities/parties';
import type { ApiPartiesGetItem } from '@/shared/types/api/parties';

type Props = {
  party: ApiPartiesGetItem | null;
  /**
   * Where to navigate after a successful delete. Defaults to /parties.
   * Pass `null` (or empty string) to skip navigation (e.g. from a list page
   * that should just refresh in place).
   */
  redirectTo?: string | null;
  onClose: () => void;
};

export const FeaturesPartiesDeleteDialog = ({
  party,
  redirectTo = '/parties',
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const remove = useEntityDelete({
    api: (id: string) => apiPartiesDelete(id),
    invalidateKeys: [queryKeys.parties.all, queryKeys.parcels.all],
    successKey: 'parties.delete.toast',
    onSuccess: () => {
      onClose();
      if (redirectTo) navigate(redirectTo);
    },
  });

  return (
    <ConfirmDialog
      open={Boolean(party)}
      title={t('parties.delete.title')}
      description={t('parties.delete.description', { name: party?.name ?? '' })}
      confirmLabel={t('parties.delete.confirm')}
      cancelLabel={t('common.cancel')}
      confirmVariant="danger"
      loading={remove.isPending}
      onConfirm={() => party && remove.mutate(party.id)}
      onCancel={onClose}
    />
  );
};
